import { AxiosError } from "axios";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Cell, CellProps, useBlockLayout, useFilters, useGlobalFilter, usePagination, useRowSelect, useSortBy, useTable } from "react-table";
import { useSticky } from "react-table-sticky";
import { changeProductAvailable_RPP24, getProductCheck_RCA94 } from "src/api/contract/contract-api";
import { useApiOperation } from "src/api/hooks";
import { getProductListAsync } from "src/api/product/product-api";
import { ProductModel, ProductStatus } from "src/api/product/product-types";
import { contract_bill_user_errors, PageMeta } from "src/api/public-types";
import { BaseCheckbox, BaseInput, BaseModal, BasePagination, BaseRadio, BaseSelect, BaseTooltip } from "src/components";
import { BaseAbstractModal } from "src/components/BaseAbstractModal";
import ProductStatusLabel from "src/components/ProductStatusLabel";
import ProductTypeChangeLabel from "src/components/ProductTypeChangeLabel";
import useApiLoading from "src/hooks/useApiLoading";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import pagePath from "src/pagePath.json";
import useContractApi from "../contract/hooks/useContractApi";

import { errorDataHandler } from "src/api/error/contract-response-error";
import BaseNewTabLink from "src/components/BaseNewTabLink";
import { useErrorModal } from "src/recoil/errorModal/hook";
import { useModal } from "src/recoil/modalState/hook";
import { useS2Contract } from "../contract/hooks/useS2Contract";
import { contractAvailFieldLabelMap } from "../product/forms/basicInfoForm/libs";
import { productStatusOptions, ProductStatusT, productTypeOptions } from "../product/product-types";
import ProductRentalCostCell from "./columns/ProductRentalCostCell";

export type MakeContractAvailFieldLabels = {
  name: string;
  label: string;
};

const columnHeader: any = [
  {
    Header: "id",
    accessor: "id",
    width: 80,
  },
  {
    Header: "상품유형",
    accessor: "productType",
    width: 80,
    Cell: (props: Cell<ProductModel>) => <ProductTypeChangeLabel productType={props.value} />,
  },
  {
    Header: "상품명",
    accessor: "productName",
    width: 220,
    Cell: (props: Cell<ProductModel>) => {
      const detailPath = `${pagePath.product.detail.replace(":id", `${props.row.original.id}`)}`;

      return (
        <div className="w-100 text-left">
          <BaseNewTabLink path={detailPath} value={props?.value || "-"} tooltip />
        </div>
      );
    },
  },

  {
    Header: "이용료",
    accessor: "rentalCost",
    Cell: (props: Cell<ProductModel>) => <ProductRentalCostCell {...props} />,
    width: 120,
  },

  {
    Header: "건물명",
    accessor: "buildingName", // api 스펙에 없음
    width: 260,
    Cell: (props: Cell<ProductModel>) => {
      return (
        <div className="w-100 text-left">
          <BaseTooltip contents={props?.value || "-"} />
        </div>
      );
    },
  },
  {
    Header: "공개 여부",
    accessor: "status",
    width: 120,
    Cell: (props: Cell<ProductModel>) =>
      props.row.original.status ? <ProductStatusLabel status={props.row.original.status as ProductStatusT} /> : "-",
  },
];

type Props = {
  title?: string;
  description?: string;
  isOpen: boolean;
  partnerId?: string;
  isValidationCheck?: boolean;
  visibleProductStatusSelect?: boolean;
  visibleProductTypeSelect?: boolean;
  visibleContractAvailableCheckBox?: boolean;
  existProductIds?: string[];
  providerIds?: string;
  onClose?: () => void;
} & (
  | {
      isMultiSelect?: false;
      onClick?: (product: ProductModel) => void;
    }
  | {
      isMultiSelect: true;
      onClick?: (products: ProductModel[]) => void;
    }
);

interface ProductWithSelect extends ProductModel {
  __isSelected?: boolean;
  __isDisabled?: boolean;
}

const SearchSpaceProductPopup = (props: Props) => {
  const {
    title = "상품 선택",
    description,
    isOpen,
    partnerId,
    isValidationCheck = true,
    visibleProductStatusSelect = false,
    visibleProductTypeSelect = false,
    visibleContractAvailableCheckBox = true,
    existProductIds,
    providerIds,
    onClose,
  } = props || {};

  const defaultParams = useRef({
    page: 0,
    size: 20,
    searchValue: "",
    sort: {
      orders: [
        {
          property: "id",
          direction: "DESC",
        },
      ],
    },
  }).current;

  const { isS2Contract } = useS2Contract();
  const { getProviderData } = useContractApi();
  const { isApiLoading } = useApiLoading();
  const { setBaseModal } = useModal();
  const { openErrorModal } = useErrorModal();
  const { isAuthority } = usePartnerAuthority();

  // 선택된 상품 목록
  const [selectedProducts, setSelectedProducts] = useState<ProductModel[]>([]);

  const [isContractAvail, setIsContractAvail] = useState<boolean>(true);

  const [products, setProducts] = useState<Array<ProductModel>>([]);

  // 페이지 메타 정보 상태
  const [pageMeta, setPageMeta] = useState<PageMeta>();
  const { executeAsync: getProductList } = useApiOperation(getProductListAsync);

  // 검색 키워드 상태

  // 계약 가능 여부는 실패 응답의 errorCode 에 기반해 안내문구를 재정의 하므로 공통 에러 모달을 쓰지 않는다
  const { executeAsync: getProductCheck } = useApiOperation(getProductCheck_RCA94, { noErrorModal: true });
  const { executeAsync: changeProductAvailable } = useApiOperation(changeProductAvailable_RPP24);

  const [keyword, setKeyword] = useState("");
  // 메시지 상태
  const [message, setMessage] = useState("");
  // 에러 상태
  const [error, setError] = useState(false);
  const [error2, setError2] = useState(false);

  const openCustomErrorModel = (response: any) => {
    let errorList: string[] = [];

    if (response?.data?.meta?.errorCode === "eCT215") {
      try {
        // 1. 에러 데이터 파싱
        const origin = JSON.parse(response?.data?.meta?.errorData);

        // 2. 문자열에서 배열 추출 및 정제
        const missingProductAttrKeyList = origin?.missingProductAttrKeyList
          ?.replace(/[\[\]"]/g, "") // [], " 제거
          ?.split(",") // 콤마로 분리
          ?.map((key: string) => key.trim()); // 공백 제거

        // 3. 매핑되는 label만 필터링하여 반환
        missingProductAttrKeyList
          ?.map((key: string) => contractAvailFieldLabelMap.find((label) => label.name === key))
          ?.filter((label: MakeContractAvailFieldLabels): label is MakeContractAvailFieldLabels => label !== undefined)
          ?.forEach((label: MakeContractAvailFieldLabels) => errorList.push(label.label));
      } catch (error) {
        errorList.push("에러 데이터 파싱 실패 : eCT215");
      }
    }
    // s2에서 공용공간이 모두 비활성화인 경우 개별 핸들링
    if (response?.data?.meta?.errorCode === "eCT218") {
      errorList.push("공용공간 비활성화");
    }
    // s2에서 공용공간이 활성화지만 공용공간을 미등록한 경우 개별 핸들링
    if (response?.data?.meta?.errorCode === "eCT219") {
      errorList.push("공용공간 미등록");
    }

    setBaseModal({
      isOpen: true,
      title: `계약이 가능하지 않습니다.`,
      children: (
        <div className="flex-d flex-row">
          <p>아래의 필수 정보가 입력되지 않았습니다.</p>
          <p>확인 후 다시 시도해주세요</p>
          <div className="w-100 text-left py10 px15 mt20 overflow-scroll" style={{ backgroundColor: "#F2F4F6", maxHeight: 250 }}>
            {errorList.length > 0 &&
              errorList.map((error: string) => {
                return (
                  <div key={error}>
                    • <span>{error}</span>
                  </div>
                );
              })}
          </div>
        </div>
      ),
      btnRightTitle: "확인",
    });
  };

  // 에러 처리
  const handleProductCheckError = (error: unknown) => {
    const response = error instanceof AxiosError ? error.response : undefined;
    if (!response) {
      openErrorModal({ cause: error });
      return;
    }

    const errorCode = response.data?.meta?.errorCode;
    if (errorCode === "eCT215" || errorCode === "eCT218" || errorCode === "eCT219") {
      openCustomErrorModel(response);
    } else {
      const { message } = errorDataHandler(response, contract_bill_user_errors);
      setMessage(message);
      setError(true);
    }
  };

  const [params, setParams] = useState<any>({
    page: 0,
    size: 20,
    ...(visibleContractAvailableCheckBox && {
      isContractAvail: isContractAvail,
    }),
    searchValue: "",
    sort: {
      orders: [
        {
          property: "id",
          direction: "DESC",
        },
      ],
    },
  });

  const productTableData: Array<ProductWithSelect> = useMemo(() => {
    return products.map((product) => {
      const isSelected =
        selectedProducts.some((selected) => String(selected.id) === String(product.id)) || existProductIds?.includes(String(product.id));

      const isDisabled = (isValidationCheck && product.isContractAvail === false) || (existProductIds || []).includes(String(product.id));

      return {
        ...product,
        __isSelected: isSelected,
        __isDisabled: isDisabled,
      };
    });
  }, [existProductIds, isValidationCheck, products, selectedProducts]);

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } = useTable(
    {
      columns: columnHeader,
      data: productTableData,
    },

    useBlockLayout,
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useSticky,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns: Array<any>) => [
        {
          Header: "선택",
          width: 60,
          Cell: ({ row }: CellProps<ProductWithSelect, any>) => {
            const id = row.original.id;
            const isDisabled = row.original.__isDisabled || false;
            const isSelected = row.original.__isSelected || false;

            if (!!props.isMultiSelect) {
              return (
                <div>
                  <BaseCheckbox
                    id={`${row.original.id}`}
                    name={`${row.original.id}`}
                    disabled={isDisabled}
                    checked={isSelected}
                    onChange={(value: boolean) => {
                      setProducts((prevProducts) => {
                        const nextProducts = prevProducts.map((product) =>
                          product.id === row.original.id ? { ...product, isSelected: value } : product,
                        );

                        return nextProducts;
                      });

                      if (value) {
                        const productAddItem = _.omit(row.original, "__isSelected", "__isDisabled") as ProductModel;
                        setSelectedProducts((prev) => [...prev, productAddItem]);
                      } else {
                        setSelectedProducts((prev) => prev.filter((product) => String(product.id) !== String(id)));
                      }
                    }}
                  />
                </div>
              );
            } else {
              return (
                <div>
                  <BaseRadio
                    id={`selector${row.original.id}`}
                    name={`selector${row.original.id}`}
                    checked={isSelected}
                    onChange={(value: boolean) => {
                      setProducts((prevProducts) => {
                        const nextProducts = prevProducts.map((product) =>
                          product.id === row.original.id ? { ...product, isSelected: value } : { ...product, isSelected: false },
                        );

                        return nextProducts;
                      });

                      if (value) {
                        const productAddItem = _.omit(row.original, "__isSelected", "__isDisabled") as ProductModel;
                        setSelectedProducts([productAddItem]);
                      } else {
                        setSelectedProducts((prev) => prev.filter((product) => String(product.id) !== String(id)));
                      }
                    }}
                    disabled={isDisabled}
                  />
                </div>
              );
            }
          },
        },
        ...columns,
      ]);
    },
  );
  // columns width 의 총 합 (table witdh 를 구하기 위함)
  const tableWidth = useMemo(() => {
    let totalWidth = 0;
    headerGroups.forEach((headerGroup) => {
      headerGroup.headers.forEach((header) => (totalWidth += Number(header?.width || 0)));
    });
    return totalWidth;
  }, [headerGroups]);

  const callList = useCallback(
    async (param: any) => {
      setProducts([]);
      const response = await getProductList(param);
      setProducts(response?.data?.data?.content || []);
      setPageMeta(response?.data?.meta?.pageMeta);
    },
    [getProductList],
  );

  const callProductList = useCallback(() => {
    let prs: { [props: string]: any } = _.cloneDeep(defaultParams);
    prs.page = 0;

    if (visibleContractAvailableCheckBox) {
      prs.isContractAvail = isContractAvail;
    }

    // 계약 S2 페이지면 TIME_COURT 상품만 조회
    if (isS2Contract) {
      prs.productType = "TIME_COURT";
    }

    if (partnerId !== undefined) {
      prs.partnerId = partnerId;
    }

    if (providerIds) {
      prs.providerIds = providerIds;
    }

    setKeyword("");
    callList(prs);
    setParams(prs);
  }, [callList, partnerId, isS2Contract, visibleContractAvailableCheckBox, isContractAvail, defaultParams, providerIds]);

  const openContractUnavailableModal = () => {
    setBaseModal({
      isOpen: true,
      btnRightTitle: "확인",
      title: "계약을 생성할 수 없습니다.",
      children: "선택하신 상품은 계약이 불가능한 상태입니다. 계약이 가능한 상품인지 확인하시려면, 상품 화면에서 계약 가능 여부 상태를 확인해 주세요.",
    });
  };

  const onCompleteData = async () => {
    const product = selectedProducts?.[0];

    if (!product?.id) {
      return;
    }

    if (isValidationCheck) {
      setMessage("");
      setError2(false); // 에러 상태 초기화

      // 계약 가능 여부 검증
      let productCheckResponse;
      try {
        productCheckResponse = await getProductCheck({ productId: String(product.id) });
      } catch (error) {
        handleProductCheckError(error);
        return;
      }

      //상품객체의 계약 가능 여부 검증
      if (product.isContractAvail === false) {
        openContractUnavailableModal();
        return;
      }

      if (!productCheckResponse.data.data.productInfo.isValidated) {
        // RPP24(상품 계약가능여부 변경)는 PRODUCT 쓰기 권한이 있을 때만 보정.
        if (isAuthority("w", "PRODUCT")) {
          await changeProductAvailable({ product: { id: product.id, isContractAvail: false } });
        }
        // 성공 응답에는 errorCode 가 없어 openCustomErrorModel 은 쓰지 않는다
        openContractUnavailableModal();
        callProductList(); // 상품 데이터 재로드
        return;
      }
    }

    if (!props.onClick) {
      return;
    }

    if (props.isMultiSelect) {
      console.debug("selectedProducts", selectedProducts);
      props.onClick(selectedProducts);
    } else {
      console.debug("resultProduct", product);
      props.onClick(product);
    }
  };

  const closeData = async () => {
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      setProducts([]);
      setSelectedProducts([]);
      callProductList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <BaseAbstractModal className="dialog-modal" isOpen={isOpen}>
      <>
        <section className={`base-abstract-modal__title ${!!description ? "pb0" : ""}`}>
          <div className="flex-row flex-center-between w-100">
            <h1>{title}</h1>
          </div>
          {visibleProductStatusSelect && (
            <div className="minmax120 mr8">
              <BaseSelect
                value={params.status || ""}
                stateOptions={productStatusOptions}
                setStateValue={(value: ProductStatus) => {
                  const nextParams = { ...params, status: value, page: 0 };
                  setParams(nextParams);
                  callList(nextParams);
                }}
                placeholder="공개상태"
              />
            </div>
          )}
          {visibleProductTypeSelect && (
            <div className="minmax120 mr8">
              <BaseSelect
                value={params.productType || ""}
                stateOptions={productTypeOptions}
                setStateValue={(value: string) => {
                  const nextParams = { ...params, productType: value, page: 0 };
                  setParams(nextParams);
                  callList(nextParams);
                }}
                placeholder="상품 유형"
              />
            </div>
          )}
          {visibleContractAvailableCheckBox && (
            <div className="minmax90 text-left">
              <BaseCheckbox
                id="isContractAvail"
                className="mr10 px0 mx0"
                label={"선택 가능"}
                name="isContractAvail"
                checked={isContractAvail}
                onChange={(value: boolean) => {
                  setIsContractAvail(value);
                  setParams({ ...params, ...{ isContractAvail: value } });
                  callList({ ...params, ...{ isContractAvail: value } });
                }}
              />
            </div>
          )}
          <div className="minmax240 text-left">
            <BaseInput
              type="text"
              value={keyword || ""}
              placeholder="검색어를 입력해주세요"
              onKeyUp={() => {
                callList({ ...params, ...{ keyword }, ...{ page: 0 } });
                setParams({ ...params, ...{ keyword }, ...{ page: 0 } });
              }}
              onChange={(value: string) => {
                setKeyword(value);
              }}
              onSearchClick={() => {
                callList({ ...params, ...{ keyword }, ...{ page: 0 } });
                setParams({ ...params, ...{ keyword }, ...{ page: 0 } });
              }}
              onClearClick={() => {
                setKeyword("");
                setParams({ ...params, ...{ keyword: "" }, ...{ page: 0 } });
              }}
            />
          </div>
        </section>
        {!!description && (
          <section className="base-abstract-modal__desc">
            <p>{description}</p>
          </section>
        )}
        {/* table */}
        <section className="base-abstract-modal__contents">
          <div {...getTableProps()} className="base-table view-data-table sticky px30">
            <div className="header">
              {headerGroups.map((headerGroup) => (
                <div {...headerGroup.getHeaderGroupProps()} className="base-table__tr">
                  {headerGroup.headers.map((header) => {
                    return (
                      <div {...header.getHeaderProps()} className="base-table__th">
                        {header.render("Header")}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            <div {...getTableBodyProps()} className="body">
              {rows.map((row) => {
                prepareRow(row);
                return (
                  <div {...row.getRowProps()} className={`base-table__tr ${row.values.floor ? "bg-gray100" : ""}`}>
                    {row.cells.map((cell) => {
                      return (
                        <div {...cell.getCellProps()} className="base-table__td">
                          {cell.render("Cell")}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
              <div>
                {rows.length === 0 && (
                  <div className="base-table__tr table-cursor-unset" style={{ width: tableWidth }}>
                    <div className="base-table__td w-100 text-center">
                      <div className="w-100">{!isApiLoading() && <span>데이터가 없습니다.</span>}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
        <section className="px30">
          <BasePagination
            pageIndex={params.page || 0}
            totalPages={pageMeta?.totalPages || 0}
            currentSize={Number(params.size) || 20}
            sizeOption={(sizeValue) => {
              setParams({ ...params, size: sizeValue });
              callList({ ...params, size: sizeValue });
            }}
            totalElements={pageMeta?.totalElements || 0}
            goPage={(page: number) => {
              setParams({ ...params, ...{ page } });
              callList({ ...params, ...{ page } });
            }}
            children={
              <div className="flex-center">
                <button className="base-btn color-white mr10" onClick={() => closeData()}>
                  취소
                </button>
                <button className="base-btn" onClick={() => onCompleteData()}>
                  완료
                </button>
              </div>
            }
          />
        </section>
        <BaseModal isOpen={error} btnRightTitle={"확인"} onClick={() => setError(false)} title={message}></BaseModal>
      </>
    </BaseAbstractModal>
  );
};

export default SearchSpaceProductPopup;
